import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import { TransactionTypes, TransactionTypesPermalink } from './models/transactions-enum';
import { DataObject } from './interfaces/data-objects';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private auth: AuthService) { }

  private mergeOptions(options?: HttpHeaders | any): object {
    let defaultHeaders = new HttpHeaders();

    if (!options) {
      options = {};
    } else if (options.headers !== undefined && options.headers !== null) {
      defaultHeaders = options.headers;
    }

    const mockToken = environment.mockToken;
    const jwtToken = this.auth.getAuthToken();

    if (mockToken && mockToken !== '') {
      defaultHeaders = defaultHeaders.set('Authorization', 'Token ' + mockToken);
    } else if (jwtToken) {
      defaultHeaders = defaultHeaders.set('Authorization', 'JWT ' + jwtToken);
    }
    const userLocale = window.localStorage.getItem('user_locale') || 'en-US';
    switch (userLocale) {
      case 'zh':
        defaultHeaders = defaultHeaders.set('accept-language', 'zh-hans');
        break;
      case 'ja':
        defaultHeaders = defaultHeaders.set('accept-language', 'ja');
        break;
      case 'zh-Hant':
        defaultHeaders = defaultHeaders.set('accept-language', 'zh-hant');
        break;
      default:
        defaultHeaders = defaultHeaders.set('accept-language', 'en');
        break;
    }
    return {
      headers: defaultHeaders,
      ...options
    };
  }

  handleError(error: HttpErrorResponse | any) {
    let errMsg: string;
    if (error instanceof HttpErrorResponse) {
      errMsg = `${error.status} - ${error.statusText || ''} ${JSON.stringify(error.error)}`;
      // handle unauthorized requests. most likely by removing token and logging user out.
      if (error.status === 401 || error.status === 403) {
        this.auth.deleteToken();

        // only redirect if the 403 was not from getting the JWT
        const isAuthFailure = (error.url.indexOf('jwt-exchange') >= 0);
        console.log('is auth failure: ', isAuthFailure);
        if (!isAuthFailure) {
          this.auth.redirectToLoginUrl();
        }
      }
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    // console.log('API Error: ', errMsg);
    return of({ error });
  }

  get(path: string, options?: any | null): Observable<Response | any> {
    const expandedHeaders = this.mergeOptions(options);
    return this.http
      .get(this.getPortwithURL(path), expandedHeaders).pipe()
      .pipe(catchError(err => this.handleError(err)));
  }

  post(path: string, body: any, options?: any | null): Observable<Response | any> {
    const expandedHeaders = this.mergeOptions(options);
    return this.http
      .post(this.getPortwithURL(path), body, expandedHeaders)
      .pipe(catchError(err => this.handleError(err)));
  }

  put(path: string, body: any, options?: any | null): Observable<Response | any> {
    const expandedHeaders = this.mergeOptions(options);
    return this.http
      .put(this.getPortwithURL(path), body, expandedHeaders)
      .pipe(catchError(err => this.handleError(err)));
  }

  delete(path: string, options?: any | null): Observable<Response | any> {
    return this.http
      .delete(this.getPortwithURL(path), this.mergeOptions(options))
      .pipe(catchError(err => this.handleError(err)));
  }

  getAuthToken(ssoCode: string): Observable<DataObject> {
    return this.post('/jwt-exchange/', {
      code: ssoCode
    });
  }

  getPortwithURL(path: string): string {
    const port = this.auth.getPort() || '8081';

    if (environment.apiUrl.endsWith(':') && environment.datadog.env === 'local') {
      return `${environment.apiUrl}${port}${path}`;
    }

    if (port === '8081') {
      return `${environment.apiUrl}${path}`;
    } else {
      return `${environment.apiChinaUrl}${path}`;
    }
  }

  getTransactionEndpoint(transaction: DataObject): string {
    let transactionEndpoint = '';
    switch (transaction.transaction_type) {
      case TransactionTypes.SALE:
      case `${TransactionTypes.SALE}s`:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.SALE];
        break;
      case TransactionTypes.LEASE:
      case `${TransactionTypes.LEASE}s`:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.LEASE];
        break;
      case TransactionTypes.EXPENSE:
      case `${TransactionTypes.EXPENSE}s`:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.EXPENSE];
        break;
      case TransactionTypes.CONSULTING:
      case TransactionTypes.CONSULTATION:
      case `${TransactionTypes.CONSULTATION}s`:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.CONSULTATION];
        break;
      case TransactionTypes.DSF:
      case `${TransactionTypes.DSF}s`:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.DSF];
        break;
      case TransactionTypes.RESI:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.RESI];
        break;
      case TransactionTypes.VALUATION:
      case `${TransactionTypes.VALUATION}s`:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.VALUATION];
        break;
      case TransactionTypes.VALUATION:
      case `${TransactionTypes.VALUATION}s`:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.VALUATION];
        break;
      default:
        transactionEndpoint = TransactionTypesPermalink[TransactionTypes.SALE];
        break;
    }
    return transactionEndpoint;
  }

}
